import { toast } from "sonner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
// Types for constants

export const isMobile = (val: string): boolean => {
  // 10-digit mobile number
  return /^\d*$/.test(val) && val.length === 10;
};

export const isNumber = (val: string, len?: number): boolean => {
  // Numeric value of certain length
  if (len !== undefined) {
    return /^\d*$/.test(val) && val.length === len;
  }
  return /^\d*$/.test(val);
};

export const isValidPassword = (val: string): boolean => {
  // Check if the text contains at least one lowercase letter, one uppercase letter, one number, and is at least 6 characters long
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
  return regex.test(val);
};

export const isAlphanumeric = (val: string): boolean => {
  // Check if the string contains only letters and numbers
  const regex = /^[a-zA-Z0-9]+$/;
  return regex.test(val);
};

export const handleCopy = (text: string): void => {
  // Create a textarea element to copy the text
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);

  // Select the text in the textarea
  textarea.select();

  // Copy the selected text to the clipboard
  document.execCommand("copy");

  // Remove the textarea element
  document.body.removeChild(textarea);

  // Alert the user that the text has been copied
  toast("Text copied to clipboard!");
};

export function removeNonNumeric(str: string): string {
  return str.replace(/\D/g, "");
}

export function formatToRupee(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);
}

export function getRandomNumbers(len: number, till: number = 25): number[] {
  const length = len % till;
  const availableNumbers = Array.from(Array(till).keys());
  const result: number[] = [];

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * availableNumbers.length);
    result.push(availableNumbers[randomIndex]);
    availableNumbers.splice(randomIndex, 1); // Remove the selected number to avoid repetition
  }

  return result;
}

export function getRandomNumber(max: number): number {
  return Math.floor(Math.random() * max);
}

export function totalSeconds(): number {
  const date = new Date(); // current date
  return Math.floor(date.getTime() / 1000);
}

export const compressText = (id: string): string => {
  if (!id) return ""; // Handle the case if the id is null or undefined
  return `${id.slice(0, 4)}...${id.slice(-3)}`;
};

// Encode function
export function encodeBase64(input: string): string {
  return btoa(input); // Converts the string into Base64
}

// Decode function
export function decodeBase64(encodedInput: string): string | 0 {
  try {
    const cleanedInput = encodedInput?.trim().replace(/\s/g, "");

    const padLength = 4 - (cleanedInput.length % 4);
    const paddedInput =
      padLength < 4 ? cleanedInput + "=".repeat(padLength) : cleanedInput;

    return atob(paddedInput);
  } catch (error) {
    return 0;
  }
}

export function popupNavigation(nav: string): string {
  return "?popup=" + encodeBase64(nav);
}

export const openWhatsApp = (message: string, number?: string) => {
  const url = `https://wa.me/${number ?? ""}?text=${encodeURIComponent(
    message
  )}`;
  window.open(url, "_blank");
};

export const sendEmail = (message: string) => {
  window.location.href = `mailto:?subject=Shared Message&body=${encodeURIComponent(
    message
  )}`;
};

export const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);

  const datePart = date.toLocaleDateString("en-GB");
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} ${timePart}`;
};

export const exportToExcel = (data: any[], baseFileName: string) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const excelBlob = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  // const fileName = getTimestampedName(baseFileName);
  saveAs(excelBlob, `${baseFileName}.xlsx`);
};

export const getErrorMessage = (err: any) => {
  const e = err?.response?.data?.error;
  if (!e) return "Something went wrong";
  if (typeof e === "string") return e;
  return e.message || "Something went wrong";
};

export const formatRoleForDisplay = (role: string) => {
  return role
    .replace("ROLE_", "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const queryLink = (branchCode: string) => {
  return `${window.location.origin}/queries/queries-form/${branchCode}`;
};
export const queryMessageBuilder = (branchName: string, branchCode: string) => {
  const link = `${window.location.origin}/queries/queries-form/${branchCode}`;

  return `Dear Parent,
Greetings from ${branchName},

To begin your journey with us, please submit your enquiry here:

${link}

We look forward to supporting your child’s educational journey.

Warm regards,
${branchName}

Powered by Empedance Consultancy Services Pvt. Ltd.`;
};

export const convertTimeToPayload = (time: string) => {
  const [hour, minute] = time.split(":").map(Number);

  return {
    hour,
    minute,
    second: 0,
    nano: 0,
  };
};

export const isOlderThan15Days = (date: string) => {
  const today = new Date();
  const selected = new Date(date);

  const diffTime = today.getTime() - selected.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return diffDays > 15;
};

export const getDaysLeft = (endDate: string) => {
  const diff =
    new Date(endDate).getTime() - new Date().getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};
